// The user's GitHub connection: where they can see it, start it and end it.
//
// A row on the account page (`/account`), the twin of `FigmaConnection`.
// GitHub is a connected account, not a login: the user signs in to Loom with
// GitLab, and connecting GitHub is what puts their GitHub projects into the
// Focus project picker (`GET /api/projects?github=1`).
//
// Everything it shows comes from `GET /api/github/status`:
//
//   - nothing       the route 404s. `server.ts` registers the GitHub routes
//                   only with LOOM_GITHUB_ENABLE and both client credentials,
//                   so a 404 means GitHub is off in this environment
//                   (`onAvailability("off")`). Any other failed read renders
//                   nothing too, and reports `"failed"` so the page can say so.
//   - not connected "Connect GitHub", a full-page navigation to
//                   `/auth/github/start` with this page as `returnTo`.
//   - connected     the GitHub login when GitHub gave one, a quiet Reconnect,
//                   and "Disconnect". A credential Loom can no longer use
//                   (`usable: false`) says so, and Reconnect is the primary
//                   button. Reconnect stays offered while `usable` is true
//                   because `usable` only sees an expired credential: a grant
//                   revoked at github.com still reads as usable, and a new
//                   OAuth round trip repairs it without a disconnect first.
//
// DISCONNECT ASKS FIRST, for Figma's reason: the way back is a GitHub login.
// The first click opens a small card inside the row; only the second writes.
// Focus goes to Cancel, Escape backs out, and focus returns to the trigger.
//
// The server revokes Loom's grant at GitHub and deletes its copy whatever the
// revoke did (`revokedAtGitHub`). Only when the revoke did not happen does the
// outcome line say where GitHub still lists Loom.
//
// A Connect started here comes back here with the callback's `?github=`
// marker: the outcome line reads it (a cancelled or refused connect otherwise
// leaves no trace), and the marker is stripped from the URL so a reload does
// not show an old error again. `URLSearchParams`, never a regex: `vite.config`
// pins safari14, where a lookbehind group throws at runtime.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { z } from "zod";

import { useT } from "../i18n/index.js";
import { httpGet, httpPost, HttpError } from "../lib/http";
import { useHereLocation } from "../focus/figma/useHereLocation";

/** The query key `/auth/github/callback` redirects back with. */
export const GITHUB_RESULT_PARAM = "github";

/**
 * The callback's outcome vocabulary, the same four machine words as Figma's.
 * Success gets a line too: it says where the new projects are, beside the
 * page's button to them. An unknown word renders nothing rather than a code.
 */
const RESULT_COPY: Readonly<Record<string, string | null>> = {
  connected: "focus.github.result.connected",
  access_denied: "focus.github.result.accessDenied",
  oauth_error: "focus.github.result.oauthError",
  exchange_failed: "focus.github.result.exchangeFailed",
};

const githubResultCopyKey = (raw: string | null): string | null => {
  if (raw === null) return null;
  if (!Object.prototype.hasOwnProperty.call(RESULT_COPY, raw)) return null;
  return RESULT_COPY[raw] ?? null;
};

/** The path to hand `returnTo`, without a stale `github` marker. */
const githubReturnToPath = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(GITHUB_RESULT_PARAM);
  const rest = params.toString();
  return rest === "" ? pathname : `${pathname}?${rest}`;
};

/** `/auth/github/start` carrying a same-origin path; the server re-validates. */
const githubConnectHref = (returnTo: string): string =>
  `/auth/github/start?returnTo=${encodeURIComponent(returnTo)}`;

const GitHubStatusSchema = z.object({
  connected: z.boolean(),
  usable: z.boolean().optional(),
  login: z.string().nullable().optional(),
});
export type GitHubStatus = z.infer<typeof GitHubStatusSchema>;

/** The one reader of `GET /api/github/status`, shared with the picker's
 *  lapsed-connection hint so both parse it with the same schema. */
export const readGitHubStatus = (): Promise<GitHubStatus> =>
  httpGet("/api/github/status", GitHubStatusSchema);

/** What a connection row tells the page once its status read settles. */
export type ConnectionAvailability = "off" | "shown" | "failed";

const DisconnectSchema = z.object({
  ok: z.boolean(),
  revokedAtGitHub: z.boolean().optional(),
});

type View =
  | { readonly kind: "loading" }
  | { readonly kind: "hidden" }
  | { readonly kind: "not_connected" }
  | {
      readonly kind: "connected";
      readonly login: string | null;
      readonly usable: boolean;
    };

type Note =
  | { readonly kind: "disconnected"; readonly stillListedAtGitHub: boolean }
  | { readonly kind: "failed" }
  | null;

export function GitHubConnection({
  location,
  onAvailability,
  onConnected,
}: {
  /** Test seam, as on `FigmaConnection`. A handed-in location is never
   *  written back. */
  readonly location?: { readonly pathname: string; readonly search: string };
  /** Once per settled status read: `"off"` on a 404 (GitHub is off here),
   *  `"failed"` on any other error, `"shown"` when the row renders. */
  readonly onAvailability?: (availability: ConnectionAvailability) => void;
  /** The callback came back with `?github=connected`. */
  readonly onConnected?: () => void;
}) {
  const t = useT();
  // Refs, so a parent's inline callback neither re-runs the effects below nor
  // goes stale inside them.
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const live = useHereLocation();
  const here = location ?? live.here;
  const [resultKey, setResultKey] = useState<string | null>(() =>
    githubResultCopyKey(
      new URLSearchParams(here.search).get(GITHUB_RESULT_PARAM),
    ),
  );
  const replaceUrl = live.replace;
  const overridden = location !== undefined;
  const hash = overridden ? "" : live.here.hash;
  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(GITHUB_RESULT_PARAM)) return;
    setResultKey(githubResultCopyKey(params.get(GITHUB_RESULT_PARAM)));
    if (params.get(GITHUB_RESULT_PARAM) === "connected") {
      onConnectedRef.current?.();
    }
    if (overridden) return;
    replaceUrl(`${githubReturnToPath(here.pathname, here.search)}${hash}`);
  }, [here.pathname, here.search, hash, overridden, replaceUrl]);
  const [view, setView] = useState<View>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const [confirming, setConfirming] = useState(false);
  const alive = useRef(true);
  const busyRef = useRef(false);
  // A status read that started before a disconnect cannot land after it.
  const generation = useRef(0);
  const headId = useId();
  const titleId = useId();
  const bodyId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const connectRef = useRef<HTMLAnchorElement | null>(null);
  const focusNext = useRef<
    "cancel" | "trigger" | "close" | "connect" | null
  >(null);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++generation.current;
    let next: View;
    let availability: ConnectionAvailability;
    try {
      const status = await readGitHubStatus();
      next = status.connected
        ? {
            kind: "connected",
            login: status.login ?? null,
            usable: status.usable ?? true,
          }
        : { kind: "not_connected" };
      availability = "shown";
    } catch (err) {
      next = { kind: "hidden" };
      availability =
        err instanceof HttpError && err.status === 404 ? "off" : "failed";
    }
    if (alive.current && mine === generation.current) {
      setView(next);
      onAvailabilityRef.current?.(availability);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    const el =
      target === "cancel"
        ? cancelRef.current
        : target === "trigger"
          ? triggerRef.current
          : target === "connect"
            ? connectRef.current
            : closeRef.current;
    if (el === null) return;
    focusNext.current = null;
    el.focus();
  });

  const askToDisconnect = (): void => {
    setNote(null);
    setResultKey(null);
    focusNext.current = "cancel";
    setConfirming(true);
  };

  const cancelDisconnect = (): void => {
    if (busyRef.current) return;
    focusNext.current = "trigger";
    setConfirming(false);
  };

  const disconnect = async (): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNote(null);
    try {
      const res = await httpPost(
        "/auth/github/disconnect",
        {},
        DisconnectSchema,
      );
      if (alive.current) {
        generation.current += 1;
        setView({ kind: "not_connected" });
        setNote({
          kind: "disconnected",
          stillListedAtGitHub: res.revokedAtGitHub !== true,
        });
      }
    } catch {
      if (alive.current) setNote({ kind: "failed" });
    } finally {
      busyRef.current = false;
      if (alive.current) {
        setBusy(false);
        focusNext.current = "close";
        setConfirming(false);
      }
    }
    // Converge from the server either way: a failed response may still have
    // deleted the row.
    await load();
  };

  if (view.kind === "loading" || view.kind === "hidden") return null;

  const connectHref = githubConnectHref(
    githubReturnToPath(here.pathname, here.search),
  );

  const sentence =
    view.kind === "not_connected"
      ? t("focus.github.connection.notConnected")
      : !view.usable
        ? t("focus.github.connection.lapsed")
        : view.login !== null
          ? t("focus.github.connection.connectedAs", { login: view.login })
          : t("focus.github.connection.connected");

  const noteText =
    note?.kind === "disconnected"
      ? note.stillListedAtGitHub
        ? `${t("focus.github.connection.disconnected")} ${t("focus.github.connection.githubStillLists")}`
        : t("focus.github.connection.disconnected")
      : note?.kind === "failed"
        ? t("focus.github.connection.disconnectFailed")
        : resultKey !== null
          ? t(resultKey)
          : "";

  return (
    <section className="account-row" aria-labelledby={headId}>
      <h3 id={headId} className="account-row-title">
        {t("account.github.title")}
      </h3>
      <p className="account-row-status">{sentence}</p>
      <div className="account-row-acts">
        {view.kind === "not_connected" && (
          <a ref={connectRef} className="focus-btn" href={connectHref}>
            {t("focus.github.connect")}
          </a>
        )}
        {view.kind === "connected" && !view.usable && (
          <a ref={connectRef} className="focus-btn" href={connectHref}>
            {t("focus.github.reconnect")}
          </a>
        )}
        {view.kind === "connected" && view.usable && (
          <a className="account-act" href={connectHref}>
            {t("focus.github.reconnect")}
          </a>
        )}
        {view.kind === "connected" && !confirming && (
          <button
            ref={triggerRef}
            type="button"
            className="account-act"
            aria-label={t("focus.github.connection.disconnectLabel")}
            onClick={askToDisconnect}
          >
            {t("focus.github.connection.disconnect")}
          </button>
        )}
      </div>
      {confirming && (
        <div
          className="account-row-confirm"
          role="group"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            cancelDisconnect();
          }}
        >
          <span id={titleId} className="account-row-confirm-title">
            {t("focus.github.connection.confirm.title")}
          </span>
          <span id={bodyId} className="account-row-confirm-body">
            {t("focus.github.connection.confirm.body")}
          </span>
          <div className="account-row-confirm-acts">
            <button
              type="button"
              className="focus-btn"
              disabled={busy}
              onClick={() => void disconnect()}
            >
              {busy
                ? t("focus.github.connection.disconnecting")
                : t("focus.github.connection.disconnect")}
            </button>
            <button
              ref={cancelRef}
              type="button"
              className="focus-btn is-ghost"
              disabled={busy}
              onClick={cancelDisconnect}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {/* Always mounted, so a screen reader announces the line when it fills. */}
      <div
        className={"account-row-note" + (noteText === "" ? " is-empty" : "")}
      >
        <p className="account-row-note-text" role="status">
          {noteText}
        </p>
        {noteText !== "" && (
          <button
            ref={closeRef}
            type="button"
            className="account-act"
            onClick={() => {
              focusNext.current =
                view.kind === "connected" ? "trigger" : "connect";
              setNote(null);
              setResultKey(null);
            }}
          >
            {t("common.close")}
          </button>
        )}
      </div>
    </section>
  );
}
